import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Formik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { IoEyeSharp, IoEyeOffSharp } from "react-icons/io5";

// components
import MainHeader from "../../components/Header/MainHeader";
import DataSafetyMessage from "../../components/Messages/DataSafetyMessage";

// methods
import AfterOnboardAPI from "../../services/AfterOnboardAPI";
import { bankDetailsValidation } from "../../validations/user.validation";
import { CustomButton } from "../../components";
import { serviceError } from "../../lib/serviceError";
import { handleShowContextualLoader, handlehideContextualLoader } from "../../utils/contextualLoaderTrigger";
import { ORDER_ID_LOCAL_STORAGE_KEY } from "../../Constants/constant";

export default function UpdateBankDetails() {
  const [loading, setLoading] = useState(false);
  const [bankData, setBankData] = useState(null);
  const [showAccountNo, setShowAccountNo] = useState(true);
  const [branchName, setBranchName] = useState("");

  const { leadType, repeatCustomer } = useSelector((state) => state.user);

  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  const { backScreen, accountId } = location.state || "";

  const { updateCustomerBankAccountAPI, getBankNameWithIFSC } = new AfterOnboardAPI();

  useEffect(() => {
    const fetchBankDetails = async () => {
      const { getCustomerBankAccountAPI } = new AfterOnboardAPI();

      const res = await getCustomerBankAccountAPI(accountId);

      if (res && res.statusCode === 200) {
        setBankData(res.data);
      }
    };

    fetchBankDetails();
  }, [repeatCustomer, leadType]);

  useEffect(() => {
    loading ? handleShowContextualLoader("Verifying your bank details…") : handlehideContextualLoader();
  }, [loading]);

   useEffect(() => {
     localStorage.removeItem(ORDER_ID_LOCAL_STORAGE_KEY);
    }, []);

  const initialValues = {
    accountHolderName: bankData?.bank_holder_name || "",
    accountNo: bankData?.accountNo || "",
    confirmedAccountNo: bankData?.accountNo || "",
    ifsc: bankData?.bankIfsc || "",
    bankName: bankData?.bank || "",
  };

  const handleIFSCChange = async (value, setFieldValue) => {
    const trimmedValue = value.trim();
    setFieldValue("ifsc", trimmedValue);

    if (trimmedValue.length === 11) {
      const res = await getBankNameWithIFSC(trimmedValue);
      if (res.statusCode === 200) {
        const bank = res?.data?.banks?.[0];
        setFieldValue("bankName", bank?.BANK || "");
        setBranchName(bank?.BRANCH || "");
        return;
      }
    }

    setFieldValue("bankName", "");
    setBranchName("");
  };

  return (
    <div className="wrapper auto-height">
      <Formik
        initialValues={initialValues}
        validationSchema={bankDetailsValidation}
        enableReinitialize={true}
        onSubmit={(values, { setSubmitting }) => {
          setSubmitting(true);
          setTimeout(async () => {
            setLoading(true);
            const res = await updateCustomerBankAccountAPI(accountId, values);
            if (res && res.statusCode === 200) {
              navigate("/bank-list");
            } else {
              serviceError(res);
            }
            setLoading(false);
            setSubmitting(false);
          }, 500);
        }}
      >
        {({ errors, values, touched, isSubmitting, handleChange, handleBlur, handleSubmit, setFieldValue }) => (
          <form onSubmit={handleSubmit} style={{ height: "100%", minHeight: "100%" }}>
            <div className="vertical-column-layout">
              <div className="vertical-column-top">
                <MainHeader title="Update Bank Details" backPageName={backScreen} />
                <p className="label">Enter your Bank Details</p>
                <p className="sub-label mb-3" style={{ marginTop: -5 }}>
                  Please Enter your Bank Details for Disbursal
                </p>

                <div className="form-group">
                  <label className="label-sm">Account Holder's Name</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter Account Holder's Name"
                    name="accountHolderName"
                    value={values.accountHolderName || ""}
                    onChange={handleChange}
                    onBlur={(e) => {
                      setFieldValue("accountHolderName", e.target.value.trim());
                    }}
                    autoComplete="off"
                  />

                  {touched.accountHolderName && errors.accountHolderName ? (
                    <div className="error-msg">{errors.accountHolderName}</div>
                  ) : null}
                </div>

                <div className="form-group">
                  <label className="label-sm">Account Number</label>
                  <input
                    type={`${showAccountNo ? "password" : "tel"}`}
                    className="form-control"
                    placeholder="Enter Account Number"
                    name="accountNo"
                    value={values.accountNo || ""}
                    onChange={(e) => {
                      setFieldValue("accountNo", e.target.value);
                    }}
                    onBlur={(e) => {
                      setFieldValue("accountNo", e.target.value.trim());
                    }}
                    autoComplete="off"
                    maxLength={18}
                  />

                  {touched.accountNo && errors.accountNo ? <div className="error-msg">{errors.accountNo}</div> : null}

                  <div style={{ position: "absolute", top: 48, right: 10 }}>
                    {showAccountNo ? (
                      <IoEyeSharp size={20} color="#7a7a7a" onClick={() => setShowAccountNo(!showAccountNo)} />
                    ) : (
                      <IoEyeOffSharp size={20} color="#7a7a7a" onClick={() => setShowAccountNo(!showAccountNo)} />
                    )}
                  </div>
                </div>

                <div className="form-group">
                  <input
                    type="tel"
                    className="form-control"
                    placeholder="Re-enter Account Number"
                    name="confirmedAccountNo"
                    value={values.confirmedAccountNo || ""}
                    onChange={(e) => {
                      setFieldValue("confirmedAccountNo", e.target.value);
                    }}
                    onBlur={(e) => {
                      setFieldValue("confirmedAccountNo", e.target.value.trim());
                    }}
                    autoComplete="off"
                    maxLength={18}
                  />

                  {touched.confirmedAccountNo && errors.confirmedAccountNo ? (
                    <div className="error-msg">{errors.confirmedAccountNo}</div>
                  ) : null}
                </div>

                <div className="form-group">
                  <label className="label-sm">IFSC Code</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter IFSC Code"
                    name="ifsc"
                    value={values.ifsc || ""}
                    onChange={async (e) => {
                      await handleIFSCChange(e.target.value, setFieldValue);
                    }}
                    onBlur={async (e) => {
                      await handleIFSCChange(e.target.value, setFieldValue);
                    }}
                    autoComplete="off"
                    maxLength={11}
                    style={{ textTransform: "uppercase" }}
                  />

                  {touched.ifsc && errors.ifsc ? <div className="error-msg">{errors.ifsc}</div> : null}
                </div>

                <div className="form-group">
                  <label className="label-sm">Bank Name</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter your Bank Name"
                    readOnly
                    name="bankName"
                    value={values.bankName || ""}
                    autoComplete="off"
                  />

                  {touched.bankName && errors.bankName ? <div className="error-msg">{errors.bankName}</div> : null}

                  {branchName && <p className="my-1">{branchName}</p>}
                </div>
              </div>

              <div className="vertical-column-bottom">
                <DataSafetyMessage />
                <CustomButton text="Next" type="submit" />
              </div>
            </div>
          </form>
        )}
      </Formik>
    </div>
  );
}
